"use client"

import { parseMappingValue } from "@/lib/parse-mapping-value"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { useSearchParams } from "next/navigation"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Combobox } from "@/components/ui/combobox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { Textarea } from "@/components/ui/textarea"
import { Loader2, Plus, Save, Trash2, X } from "lucide-react"
import { Blade, PillButton } from "@/components/blades/blade"
import { HubCard, StatusPill, ToolHeader, ToolbarPill } from "@/components/hub/hub-canvas"
import { toast } from "sonner"
import { getApiErrorDetail } from "@/lib/api"
import { useTranslation } from "@/hooks/use-translation"
import type { TranslationKey } from "@/lib/translations"
import { useValueMappingSets } from "@/hooks/use-value-mapping-sets"
import { useExplorer, useTenantAtsSystem } from "@/hooks/use-explorer"
import { codeValueService } from "@/lib/code-value-service"
import { DestinationValueInput } from "@/components/shared/destination-value-input"
import { ValueMappingDisplay } from "@/components/shared/value-mapping-display"
import { CodeTableValueType, type ValueMapping, type ValueMappingSet } from "@/types/fbo-api"
import type { AtsReferenceDataItem } from "@/types/ats-explorer-api"

const NONE_VALUE = "__none__"

function emptySet(): ValueMappingSet {
  return {
    name: "",
    description: "",
    atsReferenceType: null,
    atsReferenceValueField: "name",
    fboCodeTableName: null,
    fboCodeTableValueType: CodeTableValueType.Id,
    valueMappings: [],
  }
}

/**
 * Manager for reusable value mapping sets: named ATS -> FBO value classifications
 * (e.g. Loxo job categories -> CodeJobCategory) shared across field mappings.
 * Source values can be picked from the ATS's reference data, destination values
 * from an FBO code table — with manual entry available on both sides.
 */
export function ValueMappingSetsPage() {
  // Deep link from the explorer's reference data browser: ?set={name} opens that set
  const requestedSetName = useSearchParams().get("set")
  const { t, n } = useTranslation()
  const { loadSets, saveSet, deleteSet } = useValueMappingSets()
  const { atsSystem, error: atsSystemError } = useTenantAtsSystem()
  const { loadReferenceDataTypes, loadReferenceData } = useExplorer()

  const [sets, setSets] = useState<ValueMappingSet[]>([])
  const [draft, setDraft] = useState<ValueMappingSet | null>(null)
  // Name of the stored set being edited; null means the draft is a new set
  const [editingName, setEditingName] = useState<string | null>(null)
  const [referenceTypes, setReferenceTypes] = useState<string[]>([])
  const [referenceItems, setReferenceItems] = useState<AtsReferenceDataItem[]>([])
  const [isLoadingReferenceItems, setIsLoadingReferenceItems] = useState(false)
  const [manualSourceEntry, setManualSourceEntry] = useState(false)
  const [newSourceValue, setNewSourceValue] = useState("")
  const [newDestinationValue, setNewDestinationValue] = useState("")
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Loads once on mount, then again only when the explorer hands a set over by name. This
  // page stays mounted behind the tabs, so that hand-off arrives as a change of ?set= rather
  // than a mount, and the set may have been created moments earlier. Leaving the tab clears
  // ?set= and reloads nothing, so an edit in progress survives the trip.
  const hasLoaded = useRef(false)
  useEffect(() => {
    if (hasLoaded.current && !requestedSetName) return
    let cancelled = false
    loadSets()
      .then((loaded) => {
        if (cancelled) return
        // Marked only once a response is accepted: a cancelled first attempt, which is what
        // Strict Mode produces, must not stop the replay from loading at all
        hasLoaded.current = true
        setSets(loaded)
        const requested = requestedSetName
          ? loaded.find((s) => s.name.toLowerCase() === requestedSetName.toLowerCase())
          : undefined
        if (requested) selectSet(requested)
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : t("valueSets.messages.loadFailed"))
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedSetName])

  useEffect(() => {
    if (!atsSystem) return
    loadReferenceDataTypes(atsSystem)
      .then(setReferenceTypes)
      .catch(() => setReferenceTypes([]))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [atsSystem])

  // Load the reference values backing the source dropdown whenever the draft's type changes
  useEffect(() => {
    const referenceType = draft?.atsReferenceType
    if (!atsSystem || !referenceType) {
      setReferenceItems([])
      return
    }

    setIsLoadingReferenceItems(true)
    loadReferenceData(atsSystem, referenceType)
      .then(setReferenceItems)
      .catch(() => {
        setReferenceItems([])
        toast.error(t("valueSets.messages.referenceDataFailed", { type: referenceType }))
      })
      .finally(() => setIsLoadingReferenceItems(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [atsSystem, draft?.atsReferenceType])

  const codeTableNames = useMemo(() => codeValueService.getCodeTableNames(), [])

  const sourceOptions = useMemo(() => {
    const useId = draft?.atsReferenceValueField === "id"
    return referenceItems.map((item) => {
      const value = useId ? String(item.id) : item.name
      return { value, label: useId ? `${item.name}: ${item.id}` : item.name }
    })
  }, [referenceItems, draft?.atsReferenceValueField])

  const selectSet = (set: ValueMappingSet) => {
    setDraft(JSON.parse(JSON.stringify(set)))
    setEditingName(set.name)
    setManualSourceEntry(false)
    setNewSourceValue("")
    setNewDestinationValue("")
  }

  const startNewSet = () => {
    setDraft(emptySet())
    setEditingName(null)
    setManualSourceEntry(false)
    setNewSourceValue("")
    setNewDestinationValue("")
  }

  const updateDraft = (update: Partial<ValueMappingSet>) => {
    setDraft((current) => (current ? { ...current, ...update } : current))
  }

  const handleAddValueMapping = () => {
    if (!draft || !newSourceValue || !newDestinationValue) {
      toast.error(t("valueSets.messages.valuesRequired"))
      return
    }

    const sourceValue = parseMappingValue(newSourceValue, true)
    if (draft.valueMappings.some((vm) => String(vm.sourceValue) === String(sourceValue))) {
      toast.error(t("valueSets.messages.duplicateSourceValue", { value: newSourceValue }))
      return
    }

    const mapping: ValueMapping = { sourceValue, destinationValue: parseMappingValue(newDestinationValue, true) }
    updateDraft({ valueMappings: [...draft.valueMappings, mapping] })
    setNewSourceValue("")
    setNewDestinationValue("")
  }

  const handleRemoveValueMapping = (index: number) => {
    if (!draft) return
    updateDraft({ valueMappings: draft.valueMappings.filter((_, i) => i !== index) })
  }

  const handleSave = async () => {
    if (!draft) return

    const name = draft.name.trim()
    if (!name) {
      toast.error(t("valueSets.messages.nameRequired"))
      return
    }
    if (editingName === null && sets.some((s) => s.name.toLowerCase() === name.toLowerCase())) {
      toast.error(t("valueSets.messages.nameExists", { name }))
      return
    }

    setIsSaving(true)
    try {
      await saveSet({ ...draft, name })
      const updated = await loadSets()
      setSets(updated)
      setEditingName(name)
      toast.success(t("valueSets.messages.saved", { name }))
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("valueSets.messages.saveFailed"))
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!editingName) return
    if (!window.confirm(t("valueSets.messages.confirmDelete", { name: editingName }))) {
      return
    }

    setIsSaving(true)
    try {
      await deleteSet(editingName)
      setSets(await loadSets())
      setDraft(null)
      setEditingName(null)
      toast.success(t("valueSets.messages.deleted", { name: editingName }))
    } catch (err) {
      // Surface the server's detail — e.g. the list of field mappings still using the set
      toast.error(getApiErrorDetail(err) ?? (err instanceof Error ? err.message : t("valueSets.messages.deleteFailed")))
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="p-4">
      <ToolHeader
        title={t("valueSets.header.title")}
        description={t((sets.length === 1 ? "valueSets.header.descriptionOne" : "valueSets.header.descriptionOther"), { count: n(sets.length) })}
        actions={
          <ToolbarPill variant="primary" onClick={startNewSet} icon={<Plus className="h-3.5 w-3.5" />}>
            {t("valueSets.header.newSet")}
          </ToolbarPill>
        }
      />

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {atsSystemError && (
        <Alert className="mb-4">
          <AlertDescription>
            {t("valueSets.list.atsUnavailable", { error: atsSystemError })}
          </AlertDescription>
        </Alert>
      )}

      {isLoading && (
        <div className="flex items-center text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin mr-2" /> {t("common.state.loading")}
        </div>
      )}
      {!isLoading && sets.length === 0 && (
        <HubCard className="p-8 text-center text-sm text-muted-foreground">{t("valueSets.list.empty")}</HubCard>
      )}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
        {sets.map((set) => (
          <button key={set.name} type="button" onClick={() => selectSet(set)} className="text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-lg">
            <HubCard accent="#6b768f" className={`h-full px-4 py-3 pl-5 transition-colors hover:border-[var(--th-card-border-hover)] hover:bg-row-hover ${editingName === set.name ? "ring-1 ring-primary" : ""}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="truncate font-display text-[15px] font-bold">{set.name}</div>
                  {set.description && <div className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{set.description}</div>}
                </div>
                <StatusPill tone="active">
                  {t((set.valueMappings?.length ?? 0) === 1 ? "valueSets.card.valuesOne" : "valueSets.card.valuesOther", { count: n(set.valueMappings?.length ?? 0) })}
                </StatusPill>
              </div>
              <div className="mt-2 flex flex-wrap gap-1 font-mono text-[10px] text-muted-foreground">
                {set.atsReferenceType && <span className="rounded bg-muted px-1.5 py-0.5">{set.atsReferenceType}</span>}
                {set.fboCodeTableName && <span className="rounded bg-muted px-1.5 py-0.5">→ {set.fboCodeTableName}</span>}
              </div>
            </HubCard>
          </button>
        ))}
      </div>

      <Blade
        open={draft !== null}
        onClose={() => setDraft(null)}
        size="lg"
        eyebrow={t("valueSets.form.eyebrow")}
        title={editingName ? editingName : t("valueSets.form.newTitle")}
        description={t("valueSets.form.help")}
        footer={
          <>
            {editingName ? (
              <PillButton variant="ghost" className="text-destructive" onClick={handleDelete} disabled={isSaving}>
                <Trash2 className="h-3.5 w-3.5" /> {t("valueSets.form.deleteSet")}
              </PillButton>
            ) : (
              <span />
            )}
            <PillButton onClick={handleSave} disabled={isSaving}>
              {isSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} {t("valueSets.form.saveSet")}
            </PillButton>
          </>
        }
      >
        {draft && (
          <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="setName">{t("valueSets.form.name")}</Label>
                    <Input
                      id="setName"
                      value={draft.name}
                      onChange={(e) => updateDraft({ name: e.target.value })}
                      placeholder={t("valueSets.form.namePlaceholder")}
                      disabled={editingName !== null}
                    />
                    {editingName !== null && (
                      <p className="text-xs text-muted-foreground">
                        {t("valueSets.form.nameLocked")}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="setDescription">{t("valueSets.form.description")}</Label>
                    <Textarea
                      id="setDescription"
                      value={draft.description ?? ""}
                      onChange={(e) => updateDraft({ description: e.target.value })}
                      placeholder={t("valueSets.form.descriptionPlaceholder")}
                      rows={1}
                    />
                  </div>
                </div>

                <Separator />

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t("valueSets.form.atsSourceValues")}</Label>
                    <Select
                      value={draft.atsReferenceType ?? NONE_VALUE}
                      onValueChange={(value) =>
                        updateDraft({ atsReferenceType: value === NONE_VALUE ? null : value })
                      }
                      disabled={referenceTypes.length === 0}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t("valueSets.form.manualEntryOnly")} />
                      </SelectTrigger>
                      <SelectContent className="max-h-72">
                        <SelectItem value={NONE_VALUE}>{t("valueSets.form.manualEntryOnly")}</SelectItem>
                        {referenceTypes.map((type) => (
                          <SelectItem key={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      {t("valueSets.form.atsSourceValuesHelp")}
                    </p>
                  </div>
                  {draft.atsReferenceType && (
                    <div className="space-y-2">
                      <Label>{t("valueSets.form.sourceValueUses")}</Label>
                      <Select
                        value={draft.atsReferenceValueField ?? "name"}
                        onValueChange={(value) => updateDraft({ atsReferenceValueField: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="name">{t("valueSets.form.sourceUsesName")}</SelectItem>
                          <SelectItem value="id">{t("valueSets.form.sourceUsesId")}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t("valueSets.form.fboCodeTable")}</Label>
                    <Combobox
                      value={draft.fboCodeTableName ?? ""}
                      onValueChange={(value) => updateDraft({ fboCodeTableName: value || null })}
                      options={codeTableNames.map((name) => ({ value: name, label: name }))}
                      placeholder={t("valueSets.form.manualEntryOnly")}
                      emptyText={t("valueSets.form.noCodeTables")}
                    />
                    <p className="text-xs text-muted-foreground">
                      {t("valueSets.form.fboCodeTableHelp")}
                    </p>
                  </div>
                  {draft.fboCodeTableName && (
                    <div className="space-y-2">
                      <Label>{t("valueSets.form.destinationValueUses")}</Label>
                      <Select
                        value={String(draft.fboCodeTableValueType ?? CodeTableValueType.Id)}
                        onValueChange={(value) => updateDraft({ fboCodeTableValueType: Number(value) })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={String(CodeTableValueType.Id)}>{t("valueSets.form.destinationUsesId")}</SelectItem>
                          <SelectItem value={String(CodeTableValueType.Code)}>{t("valueSets.form.destinationUsesCode")}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                <Separator />

                <div className="space-y-3">
                  <h4 className="font-medium">{t("valueSets.form.valueMappings")}</h4>

                  {draft.valueMappings.length > 0 && (
                    <div className="border rounded-md p-3 space-y-2 max-h-64 overflow-y-auto">
                      {draft.valueMappings.map((valueMapping, index) => (
                        <div key={index} className="flex items-center justify-between">
                          <ValueMappingDisplay
                            sourceValue={valueMapping.sourceValue}
                            destinationValue={valueMapping.destinationValue}
                            codeTableName={draft.fboCodeTableName}
                            codeTableValueType={draft.fboCodeTableValueType}
                            className="font-mono bg-muted px-2 py-1 rounded"
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleRemoveValueMapping(index)}
                            className="h-6 w-6"
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="grid grid-cols-3 gap-2 items-end">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="newSourceValue">{t("valueSets.form.sourceValue")}</Label>
                        {draft.atsReferenceType && (
                          <button
                            type="button"
                            className="text-xs text-muted-foreground underline"
                            onClick={() => setManualSourceEntry((m) => !m)}
                          >
                            {t((manualSourceEntry ? "valueSets.form.pickFromList" : "valueSets.form.enterManually"))}
                          </button>
                        )}
                      </div>
                      {draft.atsReferenceType && !manualSourceEntry ? (
                        <Combobox
                          value={newSourceValue}
                          onValueChange={setNewSourceValue}
                          options={sourceOptions}
                          placeholder={isLoadingReferenceItems ? t("common.state.loading") : t("valueSets.form.selectValue")}
                          emptyText={t("valueSets.form.noReferenceValues")}
                          disabled={isLoadingReferenceItems}
                        />
                      ) : (
                        <Input
                          id="newSourceValue"
                          placeholder={t("valueSets.form.sourceValuePlaceholder")}
                          value={newSourceValue}
                          onChange={(e) => setNewSourceValue(e.target.value)}
                        />
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="newDestinationValue">{t("valueSets.form.destinationValue")}</Label>
                      <DestinationValueInput
                        value={newDestinationValue}
                        onValueChange={setNewDestinationValue}
                        codeTableName={draft.fboCodeTableName}
                        codeTableValueType={draft.fboCodeTableValueType}
                        placeholder={t("valueSets.form.destinationValuePlaceholder")}
                        id="newDestinationValue"
                      />
                    </div>
                    <Button onClick={handleAddValueMapping} size="sm">
                      <Plus className="h-4 w-4 mr-2" /> {t("valueSets.form.add")}
                    </Button>
                  </div>
                </div>

          </div>
        )}
      </Blade>
    </div>
  )
}
